/**
 * WeekBoard — week view as a door-row × day-column board.
 *
 * Doors run down the left (plus an Unassigned row), Sun–Sat across the top. Each
 * cell holds that door's appointments for that day as compact status-color
 * chips (time range + carrier). It mirrors the day board's information:
 *   - per-day limits in each column header
 *   - click a chip → detail
 *   - right-click a chip → context menu
 *   - drag a chip to another cell → reschedule to that day + door, SAME time
 *     (change the time by editing the appointment). All server safeguards apply.
 *
 * Drop target is resolved on release via elementFromPoint, so no per-cell
 * pointer wiring. Grouping uses local dates (parseISO of scheduledStartUtc),
 * matching the day board and month grid.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { format, parseISO, addDays } from 'date-fns';
import { ArrowDownWideNarrow, RotateCcw } from 'lucide-react';
import type { AppointmentDto, AppointmentStatusCode, AppointmentEventDto, CalendarDoorDto } from '@/api/types';
import type { MoveAppointmentEvent, RequestContextMenuEvent } from '@/components/GanttCalendar';
import { useAppointmentHover, LaborPulse, LaborPausedMark, LaborDoneMark, DriverBadge, PaperworkBadge, LicenceBadge } from '@/components/AppointmentHoverCard';
import { NoWmsBadge } from '@/components/WmsMarks';
import { loadSubLine } from '@/lib/loadIdentifier';
import { cn, FRAME_BORDER } from '@/lib/utils';
import { BOARD_HEADER_STRIP_CLASS } from '@/components/StatusFilterBar';
/**
 * ---- ⭐⭐ THE DOOR ORDER IS ONE MODULE, NOT TWO IMPLEMENTATIONS (2026-09-19) ----
 *
 * > Pete: *"Did we not apply the filter and ability to move doors up and down by
 * > dragging on the week view?"* — **no, and it was worse than absent.**
 *
 * ⛔⛔ THE ORDER SHE DRAGGED ON DAY IS PERSISTED IN `localStorage` UNDER
 * `dock.doorOrder.v1.<facility>`, AND THIS FILE NEVER READ THAT KEY. It sorted
 * `doors` by `displayOrder` and stopped. So a scheduler who spent a minute
 * arranging Brighton's 23 rows saw her arrangement on Day and the site's order on
 * Week, with nothing on screen explaining the difference.
 *
 *   ** A PREFERENCE THAT IS SAVED GLOBALLY AND READ IN ONE PLACE IS NOT A
 *      MISSING FEATURE ELSEWHERE. IT IS A DISAGREEMENT, AND THE VIEW THAT DOES
 *      NOT KNOW ABOUT IT IS THE ONE THAT LOOKS BROKEN. **
 *
 * ✅ Same module, same key, same helpers as `GanttCalendar.tsx`. Nothing about the
 * ordering rules is restated here — see `lib/doorOrder.ts` for why the toggle
 * never runs itself and why OFF means "the order she had", not "the server order".
 */
import {
    orderDoors, moveDoorForDrop, canDropDoorRow, readDoorOrder, writeDoorOrder, clearDoorOrder, isSiteOrder,
} from '@/lib/doorOrder';
import { DOOR_ROW_DND_MIME, isDoorRowDrag } from '@/lib/dnd';

type CapBand = {
    dayOfWeek: number;
    direction: string;
    startMinutes: number;
    endMinutes: number;
    maxConcurrent: number;
    effectiveStartDate?: string | null;
    effectiveEndDate?: string | null;
};
// A band applies on a given YYYY-MM-DD when it's within its effective range.
const bandAppliesOn = (b: CapBand, dateStr: string): boolean =>
    (!b.effectiveStartDate || dateStr >= b.effectiveStartDate) &&
    (!b.effectiveEndDate || dateStr <= b.effectiveEndDate);

const STATUS_CHIP: Record<AppointmentStatusCode, string> = {
    REQUESTED: 'bg-status-requested/85 text-white',
    APPROVED:  'bg-status-approved/85 text-white',
    ARRIVED:   'bg-status-arrived/85 text-white',
    DEPARTED:  'bg-status-departed/70 text-white',
    NOSHOW:    'bg-status-noshow/85 text-white',
    CANCELLED: 'bg-status-cancelled/40 text-white line-through',
};
const BLOCK_CHIP = 'bg-slate-500/80 text-white';

function chipClass(a: AppointmentDto): string {
    if (
        a.direction === 'BLOCK' &&
        a.statusCode !== 'CANCELLED' &&
        a.statusCode !== 'NOSHOW' &&
        a.statusCode !== 'DEPARTED'
    ) {
        return BLOCK_CHIP;
    }
    return STATUS_CHIP[a.statusCode];
}

const pad = (n: number) => String(n).padStart(2, '0');

// 1=Sun..7=Sat, matching the backend's getLocalDayOfWeek for a calendar date.
function dowOf(ds: string): number {
    return new Date(`${ds}T00:00:00.000Z`).getUTCDay() + 1;
}

function fmtMin(m: number): string {
    const h24 = Math.floor(m / 60);
    const mm = m % 60;
    const ap = h24 < 12 ? 'a' : 'p';
    let hr = h24 % 12;
    if (hr === 0) hr = 12;
    return mm === 0 ? `${hr}${ap}` : `${hr}:${pad(mm)}${ap}`;
}

function dirShort(d: string): string {
    return d === 'INBOUND' ? 'IN' : d === 'OUTBOUND' ? 'OUT' : 'ALL';
}

// Same IN/OUT badge as the day board's Limits summary.
function DirBadge({ k }: { k: 'IN' | 'OUT' }) {
    return (
        <span
            className={`inline-block rounded-sm border px-1 text-[8px] font-semibold leading-tight ${
                k === 'IN'
                    ? 'bg-blue-100 text-blue-800 border-blue-300'
                    : 'bg-orange-100 text-orange-800 border-orange-300'
            }`}
        >
            {k}
        </span>
    );
}

// Direction tag on an appointment block — matches the day board (IN / OUT / BLK).
function DirTag({ dir }: { dir: string }) {
    const label = dir === 'BLOCK' ? 'BLK' : dir === 'INBOUND' ? 'IN' : 'OUT';
    const cls =
        dir === 'BLOCK'
            ? 'bg-slate-200 text-slate-700 border-slate-400'
            : dir === 'INBOUND'
              ? 'bg-blue-100 text-blue-800 border-blue-300'
              : 'bg-orange-100 text-orange-800 border-orange-300';
    return (
        <span className={`inline-block shrink-0 rounded border px-1 text-[8px] font-semibold leading-tight ${cls}`}>
            {label}
        </span>
    );
}

// Secondary line: PO (or consignee for outbound / doc number) + pallet count —
// identical logic to the day board's block.
// It was a COPY of the day board's block, in a file that could not see it, and
// all three copies had to be found and edited together to add the container
// (2026-08-27) — which is exactly how two of them would have been missed.
// See lib/loadIdentifier.ts for why the appointment number now comes last.
const subLine = loadSubLine;

export interface WeekBoardProps {
    weekStart: string;   // yyyy-MM-dd (Sunday)
    /** ⛔ Per FACILITY -- the door order is keyed by it. Same prop the Gantt takes. */
    facilityCode: string;
    /** ⭐ The status strip's CONTENT. The frame is this component's job; see below. */
    header?: ReactNode;
    doors: CalendarDoorDto[];
    capBands: CapBand[];
    appointments: AppointmentDto[];
    onSelectAppointment: (a: AppointmentDto) => void;
    /**
     * ---- ⭐⭐ DOUBLE CLICK OPENS THE EDITOR (Pete, 2026-09-27) ----------------
     *
     * > *"Double-click on Week and Month view should pull the appointment into EDIT"*
     *
     * ⛔ A REAL `onDoubleClick`, UNLIKE THE SINGLE CLICK ON THIS BOARD, which is
     * synthesised from a pointerup that never moved (see the drag effect below). The
     * browser fires this one natively, so it cannot be confused with a reschedule —
     * the same reasoning `GanttCalendar` records for its own double click.
     *
     * ⚠️ THE SINGLE CLICK STILL FIRES FIRST and still opens the detail dialog. The
     * PAGE closes it as it opens the editor. ⛔ Do not try to suppress it here with a
     * timer: that puts a quarter-second of lag on every ordinary click to serve the
     * rarer gesture.
     *
     * ⚠️ OPTIONAL, so a caller with no editor keeps today's behaviour rather than
     * failing to compile.
     */
    onEditAppointment?: (a: AppointmentDto) => void;
    onMoveAppointment: (ev: MoveAppointmentEvent) => void;
    onRequestContextMenu: (ev: RequestContextMenuEvent) => void;
    onOpenDay: (date: string) => void;
    todayStr: string;
    fetchEvents?: (appointmentId: number) => Promise<AppointmentEventDto[]>;
}

interface DragState {
    appt: AppointmentDto;
    originX: number;
    originY: number;
    x: number;
    y: number;
    moved: boolean;
}

export function WeekBoard({
    weekStart,
    facilityCode,
    header,
    doors,
    capBands,
    appointments,
    onSelectAppointment,
    onEditAppointment,
    onMoveAppointment,
    onRequestContextMenu,
    onOpenDay,
    todayStr,
    fetchEvents,
}: WeekBoardProps) {
    const days = useMemo(
        () => Array.from({ length: 7 }, (_, i) => format(addDays(parseISO(weekStart), i), 'yyyy-MM-dd')),
        [weekStart],
    );

    // ---- Door order: the toggle, the dragged order, and the drag in flight ----
    const [busyFirst, setBusyFirst] = useState(false);
    const [manualOrder, setManualOrder] = useState<number[] | null>(
        () => readDoorOrder(facilityCode));
    const [rowDragId, setRowDragId] = useState<number | null>(null);
    const [rowOverId, setRowOverId] = useState<number | null>(null);

    /* ⚠️ RE-READ ON FACILITY CHANGE. The key is per facility, and this component is
       not remounted when the toolbar switches sites -- so without this it would keep
       showing Brighton's arrangement on Reno's doors. Same effect as the Gantt's. */
    useEffect(() => {
        setManualOrder(readDoorOrder(facilityCode));
        setBusyFirst(false);
    }, [facilityCode]);

    /**
     * ⛔ WHICH DOORS COUNT AS BUSY IS A WEEK-WIDE QUESTION HERE, NOT A DAY'S.
     *
     * The Gantt asks "does this door have an appointment TODAY". A week board that
     * asked the same question would have to pick one of its seven days to mean
     * "today", and any choice is wrong on the other six. ✅ So a door is busy if it
     * has an appointment ANYWHERE in the week on screen -- which is the only reading
     * that matches what the scheduler can actually see in the row.
     */
    const busyDoorIds = useMemo(() => {
        const set = new Set<number>();
        for (const a of appointments) if (a.dockDoorId != null) set.add(a.dockDoorId);
        return set;
    }, [appointments]);

    /* ⛔ ONE PREDICATE, THREE READERS -- the row order, the drop, and the dragover
       that decides whether the drop is legal at all. The same note as the Gantt's. */
    const hasAppointments = useCallback(
        (d: { dockDoorId: number }) => busyDoorIds.has(d.dockDoorId),
        [busyDoorIds]);

    const rows = useMemo(() => {
        /* ⛔ `orderDoors` NEVER SEES THE QUEUE ROW -- Pete: *"(not unassigned)"*.
           It is prepended AFTER the sort, exactly as the Gantt does it, so there is
           no path by which a reorder could bury the row a scheduler looks at to find
           trucks with no door. */
        const doorRows = orderDoors(doors, manualOrder, busyFirst, hasAppointments)
            .map((d) => ({
                key: String(d.dockDoorId),
                doorId: d.dockDoorId as number | null,
                code: d.code,
                name: d.name,
                dedicatedTo: d.dedicatedTo as string | null,
            }));
        /* ⛔⚠️ `code` AND `name` ARE THE OTHER WAY ROUND FROM WHAT THIS FILE USED TO
           SAY. It read `code: 'Unassigned', name: 'queue'`, which rendered correctly
           only while the CODE was the big line. The Gantt's queue row is
           `{ code: 'QUEUE', name: 'Unassigned' }`, and now that both views print the
           NAME big, this row has to agree with it or the week board's first row would
           read "queue" in bold. ** A LITERAL THAT WAS ONLY EVER RIGHT BECAUSE ITS
           RENDERER WAS ALSO WRONG. ** */
        return [{ key: 'UNASSIGNED', doorId: null as number | null, code: 'QUEUE', name: 'Unassigned', dedicatedTo: null as string | null }, ...doorRows];
    }, [doors, manualOrder, busyFirst, busyDoorIds, hasAppointments]);

    // door-key | date  ->  appointments
    const byCell = useMemo(() => {
        const m = new Map<string, AppointmentDto[]>();
        for (const a of appointments) {
            const doorKey = a.dockDoorId == null ? 'UNASSIGNED' : String(a.dockDoorId);
            const ds = format(parseISO(a.scheduledStartUtc), 'yyyy-MM-dd');
            const key = `${doorKey}|${ds}`;
            const arr = m.get(key);
            if (arr) arr.push(a);
            else m.set(key, [a]);
        }
        for (const list of m.values()) {
            list.sort(
                (a, b) =>
                    parseISO(a.scheduledStartUtc).getTime() - parseISO(b.scheduledStartUtc).getTime(),
            );
        }
        return m;
    }, [appointments]);

    const capByDow = useMemo(() => {
        const m = new Map<number, CapBand[]>();
        for (const b of capBands) {
            const arr = m.get(b.dayOfWeek);
            if (arr) arr.push(b);
            else m.set(b.dayOfWeek, [b]);
        }
        return m;
    }, [capBands]);

    const [drag, setDrag] = useState<DragState | null>(null);
    const hover = useAppointmentHover(fetchEvents, drag?.moved ?? false);

    useEffect(() => {
        if (!drag) return;
        const onMove = (e: PointerEvent) => {
            setDrag((d) =>
                d
                    ? {
                          ...d,
                          x: e.clientX,
                          y: e.clientY,
                          moved: d.moved || Math.abs(e.clientX - d.originX) > 4 || Math.abs(e.clientY - d.originY) > 4,
                      }
                    : d,
            );
        };
        const onUp = (e: PointerEvent) => {
            const x = e.clientX;
            const y = e.clientY;
            setDrag((d) => {
                if (!d) return null;
                if (!d.moved) {
                    onSelectAppointment(d.appt);
                    return null;
                }
                const cell = (document.elementFromPoint(x, y) as HTMLElement | null)?.closest('[data-cell]');
                if (cell) {
                    const doorKey = cell.getAttribute('data-door-key');
                    const ds = cell.getAttribute('data-date');
                    if (doorKey && ds) {
                        const targetDoorId = doorKey === 'UNASSIGNED' ? null : Number(doorKey);
                        const s = parseISO(d.appt.scheduledStartUtc);
                        const eDate = parseISO(d.appt.scheduledEndUtc);
                        const durMs = eDate.getTime() - s.getTime();
                        const sameDate = format(s, 'yyyy-MM-dd') === ds;
                        const sameDoor = targetDoorId === (d.appt.dockDoorId ?? null);
                        if (!sameDate || !sameDoor) {
                            const startLocal = `${ds}T${pad(s.getHours())}:${pad(s.getMinutes())}:00`;
                            const ns = new Date(startLocal);
                            const ne = new Date(ns.getTime() + durMs);
                            onMoveAppointment({
                                appointmentId: d.appt.appointmentId,
                                dockDoorId: targetDoorId,
                                newStartUtc: ns.toISOString(),
                                newEndUtc: ne.toISOString(),
                            });
                        }
                    }
                }
                return null;
            });
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, [drag, onMoveAppointment, onSelectAppointment]);

    /**
     * ⚠️ THE SAME TWO NUMBERS AND THE SAME CONDITION AS `GanttCalendar.tsx`
     * (`DOOR_LABEL_NARROW_PX` / `DOOR_LABEL_WIDE_PX`). 200 only buys room for the
     * ` ↺ Site order` reset, which renders only when the board is out of the site's
     * order; the rest of the time the column gives the 40px back.
     * ⛔ THE NUMBERS ARE NOT SHARED IN CODE — one is a constant, one is a grid
     * template. `door-order-test` asserts they agree. Change one, change both.
     */
    const doorsAreInSiteOrder = isSiteOrder(doors, manualOrder);
    const gridCols = {
        gridTemplateColumns: `${doorsAreInSiteOrder ? 160 : 200}px repeat(7, minmax(0, 1fr))`,
    };

    return (
        <div className={`flex h-full flex-col overflow-hidden rounded-lg border ${FRAME_BORDER} bg-card`} style={{ userSelect: drag?.moved ? 'none' : undefined }}>
            {/**
              * ⭐ THE STATUS STRIP, INSIDE THE CARD — exactly where the Gantt puts
              * its own (Pete, 2026-09-19: *"Should be formatted identical"*).
              * ⛔ INSIDE, not above: on the page it was full-viewport-width with no
              * band, which is why the tally sat at the window's edge and a `mb-3`
              * gap opened between it and the grid. The class is
              * `BOARD_HEADER_STRIP_CLASS`, the same string the Gantt reads.
              * ⚠️ `header` is a ReactNode rather than five filter props: the filter
              * state lives in Calendar.tsx and has no business being threaded
              * through a board that does not read it.
              */}
            {header && <div className={BOARD_HEADER_STRIP_CLASS}>{header}</div>}
            <div className="flex-1 overflow-auto">
                <div className="grid" style={gridCols}>
                    {/* Corner */}
                    {/**
                      * ---- ⭐ THE DOOR COUNT LIVES HERE TOO (Pete, 2026-09-19) ----
                      *
                      * > Pete: *"Doors on the week and month views don't have the total
                      * > Doors (xx)"*
                      *
                      * ✅ Same expression as the Gantt's header cell: `doors.length`.
                      * ⛔ **NOT `rows.length`** -- `rows` prepends the synthetic Unassigned
                      * queue row, which is not a door. Both views must print the same number
                      * or the difference reads as a data problem.
                      * ⚠️ MONTH HAS NO DOOR COLUMN AT ALL -- `MonthGrid.tsx` is a grid of
                      * DAYS, not doors, so there is nowhere for this to go and nothing it
                      * would describe. Measured, not assumed: the file contains no `door`.
                      * ⚠️ The cell is already `uppercase`, so `Doors` renders as DOORS.
                      */}
                    <div className="sticky left-0 top-0 z-40 flex h-12 items-center gap-1 border-b border-r bg-card px-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        <span className="whitespace-nowrap">Doors ({doors.length})</span>
                        {/* ⭐ THE SAME TWO CONTROLS AS THE GANTT HEADER, same order, same
                            glyphs, same wording. ⛔ The behaviour is not restated here --
                            `lib/doorOrder.ts` owns it. What IS restated is the tooltip's
                            two-state phrasing, because a control that says "back" without
                            saying back to WHAT is how this got reported as broken on
                            2026-09-10 when it was working. */}
                        <button
                            type="button"
                            onClick={() => setBusyFirst(v => !v)}
                            aria-pressed={busyFirst}
                            title={busyFirst
                                ? (!doorsAreInSiteOrder
                                    ? 'Doors with appointments are on top — click to go back to the order you dragged'
                                    : 'Doors with appointments are on top — click to go back to the site’s door order')
                                : 'Put doors with appointments on top; empty doors to the bottom'}
                            className={cn(
                                'shrink-0 rounded p-0.5 transition-colors hover:bg-muted',
                                busyFirst ? 'bg-primary/15 text-primary' : 'text-muted-foreground',
                            )}
                        >
                            <ArrowDownWideNarrow className="h-3.5 w-3.5" />
                            <span className="sr-only">
                                {busyFirst
                                    ? (!doorsAreInSiteOrder ? 'Go back to the order you dragged' : 'Go back to the site’s door order')
                                    : 'Sort doors with appointments to the top'}
                            </span>
                        </button>
                        {!doorsAreInSiteOrder && (
                            <button
                                type="button"
                                onClick={() => { clearDoorOrder(facilityCode); setManualOrder(null); }}
                                title="Forget the order you dragged and go back to the site's door order"
                                className="flex shrink-0 items-center gap-0.5 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted"
                            >
                                <RotateCcw className="h-3.5 w-3.5" />
                                <span className="text-[9px] font-medium normal-case leading-none">Site order</span>
                            </button>
                        )}
                    </div>
                    {/* Day headers with per-day limits */}
                    {days.map((ds) => {
                        const dow = dowOf(ds);
                        const bands = (capByDow.get(dow) ?? []).filter((b) => bandAppliesOn(b, ds));
                        const isToday = ds === todayStr;
                        return (
                            <button
                                key={ds}
                                onClick={() => onOpenDay(ds)}
                                title="Open day view"
                                className={`sticky top-0 z-30 flex h-12 min-w-0 items-center overflow-hidden border-b border-r px-2 text-left transition-colors hover:bg-muted/50 ${
                                    isToday ? 'bg-primary/5' : 'bg-card'
                                }`}
                            >
                                <div className="flex min-w-0 items-baseline gap-1">
                                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                        {format(parseISO(ds), 'EEE')}
                                    </span>
                                    <span className={`text-sm font-semibold ${isToday ? 'text-primary' : ''}`}>
                                        {format(parseISO(ds), 'd')}
                                    </span>
                                </div>
                                {bands.length > 0 && (
                                    <div className="absolute right-1 top-1 flex flex-col items-end gap-0.5">
                                        {bands.map((b, i) => {
                                            const blocked = b.maxConcurrent === 0;
                                            return (
                                            <span
                                                key={i}
                                                className={`inline-flex items-center gap-0.5 whitespace-nowrap text-[9px] text-muted-foreground`}
                                                title={blocked
                                                    ? `Blocked: ${dirShort(b.direction)} ${fmtMin(b.startMinutes)}–${fmtMin(b.endMinutes)} (no appointments)`
                                                    : `Limit: ${dirShort(b.direction)} ${fmtMin(b.startMinutes)}–${fmtMin(b.endMinutes)}, max ${b.maxConcurrent}`}
                                            >
                                                {b.direction === 'INBOUND' ? (
                                                    <DirBadge k="IN" />
                                                ) : b.direction === 'OUTBOUND' ? (
                                                    <DirBadge k="OUT" />
                                                ) : (
                                                    <span className="inline-flex gap-0.5">
                                                        <DirBadge k="IN" />
                                                        <DirBadge k="OUT" />
                                                    </span>
                                                )}
                                                <span className={blocked ? 'line-through' : ''}>
                                                    {fmtMin(b.startMinutes)}–{fmtMin(b.endMinutes)}·{blocked ? 'Blocked' : b.maxConcurrent}
                                                </span>
                                            </span>
                                            );
                                        })}
                                    </div>
                                )}
                            </button>
                        );
                    })}

                    {/* Door rows */}
                    {rows.map((row) => (
                        <div key={row.key} className="contents">
                            {/**
                              * ---- ⭐⭐ DRAG A DOOR ROW TO REORDER IT (Pete, 2026-09-19) ----
                              *
                              * ✅ HTML5 DRAG-AND-DROP, the same mechanism and the same custom
                              * MIME (`DOOR_ROW_DND_MIME`) the Gantt uses -- NOT the pointer-event
                              * machine this file already runs for appointment chips.
                              *
                              * ⛔⛔ THAT SEPARATION IS THE WHOLE SAFETY ARGUMENT, AND IT IS
                              * STRUCTURAL RATHER THAN CAREFUL. An appointment drag begins with
                              * `onPointerDown` on a chip inside a `[data-cell]` column; a row
                              * drag begins with `dragstart` on this sticky label cell, which is
                              * not a cell and holds no chips. Two different event systems, two
                              * disjoint sets of elements -- there is no gesture that could be
                              * read as both, and no ordering of handlers that could make one
                              * swallow the other.
                              *
                              *   ** THE GANTT NEEDED `isDoorRowDrag` BECAUSE ITS ROWS CARRY
                              *      FOUR OTHER DRAGS. IT IS KEPT HERE ANYWAY: a drop handler
                              *      that accepts whatever arrives will one day be handed a
                              *      check-in card or a scanned document. **
                              *
                              * ⛔ THE QUEUE ROW IS NOT DRAGGABLE -- Pete, 2026-09-10:
                              * *"re-arrange the Dock Door Rows (not unassigned)"*.
                              */}
                            <div
                                draggable={row.doorId !== null}
                                onDragStart={(e) => {
                                    if (row.doorId === null) { e.preventDefault(); return; }
                                    setRowDragId(row.doorId);
                                    e.dataTransfer.setData(DOOR_ROW_DND_MIME, String(row.doorId));
                                    e.dataTransfer.effectAllowed = 'move';
                                }}
                                onDragEnd={() => { setRowDragId(null); setRowOverId(null); }}
                                onDragOver={(e) => {
                                    if (!isDoorRowDrag(e.dataTransfer)) return;
                                    if (row.doorId === null) return;
                                    /* ⛔⛔ Refuse a cross-group drop with the CURSOR. See the
                                       Gantt's copy of this note and `lib/doorOrder.ts`. */
                                    if (!canDropDoorRow(doors, busyFirst, hasAppointments,
                                                        rowDragId, row.doorId)) {
                                        e.dataTransfer.dropEffect = 'none';
                                        return;
                                    }
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'move';
                                    setRowOverId(row.doorId);
                                }}
                                onDragLeave={() => setRowOverId((h) => (h === row.doorId ? null : h))}
                                onDrop={(e) => {
                                    if (!isDoorRowDrag(e.dataTransfer)) return;
                                    e.preventDefault();
                                    setRowOverId(null);
                                    if (row.doorId === null) return;
                                    const dragged = Number(e.dataTransfer.getData(DOOR_ROW_DND_MIME));
                                    if (!Number.isFinite(dragged)) return;
                                    /**
                                      * ⛔⛔⚤ ~~THE ORDER SHE IS LOOKING AT IS WHAT GETS
                                      * SAVED~~ — STRUCK 2026-09-19 (§0.3). It was half
                                      * right: saving the SERVER order would indeed
                                      * rearrange the board when she turned the sort off.
                                      * But saving the VISIBLE order froze the busy
                                      * partition into her saved arrangement, after which
                                      * the toggle could never change anything again.
                                      * ** A VIEW TOGGLE'S OUTPUT WAS PERSISTED AS A
                                      *    PREFERENCE, AND A PREFERENCE CANNOT BE TOGGLED
                                      *    OFF. **
                                      * ✅ `moveDoorInBaseOrder` applies the move to the
                                      * arrangement UNDERNEATH the partition. With the sort
                                      * off the two lists are identical, so this changes
                                      * nothing in the ordinary case.
                                      * ⛔ THE COMPOSITION LIVES IN `lib/doorOrder.ts` NOW.
                                      * Both boards used to build this id list out of their
                                      * own `rows`, which is two copies of the bug.
                                      */
                                    const next = moveDoorForDrop(
                                        doors, manualOrder, busyFirst, hasAppointments,
                                        dragged, row.doorId);
                                    if (!next) return;
                                    setManualOrder(next);
                                    writeDoorOrder(facilityCode, next);
                                    /* ⛔⛔ ~~AND THE SORT GOES OFF~~ -- STRUCK 2026-09-19, same as
                                       the Gantt's. The placement is now expressed in the
                                       partition's own terms, so the partition cannot undo it,
                                       and turning the view off was itself the six-row unwind. */
                                }}
                                className={cn(
                                    'sticky left-0 z-20 flex flex-col justify-center border-b border-r bg-card px-2 py-1',
                                    row.doorId !== null && 'cursor-grab active:cursor-grabbing',
                                    /**
                                      * ⛔⚤ `rowDragId === row.doorId` ALONE IS A BUG IN THIS FILE,
                                      * AND IT IS NOT ONE IN THE GANTT.
                                      *
                                      * The Gantt's queue row carries the NUMBER sentinel
                                      * `UNASSIGNED_DOOR_ID`, so its idle `null` state can never
                                      * match it. This file uses `doorId: null` for that row --
                                      * so `null === null` is TRUE the whole time nothing is being
                                      * dragged, and the Unassigned row would render permanently
                                      * at `opacity-40` with a primary ring around it, on first
                                      * paint, with no drag anywhere.
                                      *
                                      *   ** I COPIED A COMPARISON THAT WAS SAFE BECAUSE OF A
                                      *      SENTINEL THE DESTINATION FILE DOES NOT USE. THE
                                      *      EXPRESSION WAS IDENTICAL; THE TYPE UNDER IT WAS NOT. **
                                      *
                                      * ⚠️ `tsc` cannot see this -- both sides are `number | null`
                                      * and the comparison is perfectly legal. It was caught by
                                      * reading, not by the compiler.
                                      */
                                    row.doorId !== null && rowDragId === row.doorId && 'opacity-40',
                                    row.doorId !== null && rowOverId === row.doorId && 'ring-2 ring-inset ring-primary',
                                )}
                            >
                                {/**
                                  * ⛔⚠️ NAME BIG, CODE SMALL -- Pete, 2026-09-19, on the two views
                                  * disagreeing. This file used to print the CODE in the larger
                                  * weight, which is why its synthetic queue row was written
                                  * `{ code: 'Unassigned', name: 'queue' }` -- inverted relative to
                                  * the Gantt's. Both were fixed together; see the `rows` memo.
                                  * ✅ The code line only renders when it differs from the name,
                                  * the same condition the Gantt uses -- a door called `DD119` with
                                  * no separate name should not print `DD119` twice.
                                  */}
                                <span className="truncate text-sm font-medium leading-tight">{row.name || row.code}</span>
                                {row.name && row.name !== row.code && (
                                    <span className="truncate font-mono text-[10px] leading-tight text-muted-foreground">{row.code}</span>
                                )}
                                {row.dedicatedTo && (
                                    <span
                                        className="mt-0.5 inline-flex w-fit items-center gap-1 rounded bg-amber-100 px-1 text-[9px] font-medium text-amber-800"
                                        title={`Dedicated: ${row.dedicatedTo}`}
                                    >
                                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                                        <span className="truncate">{row.dedicatedTo}-Dedicated</span>
                                    </span>
                                )}
                            </div>
                            {days.map((ds) => {
                                const list = byCell.get(`${row.key}|${ds}`) ?? [];
                                return (
                                    <div
                                        key={ds}
                                        data-cell
                                        data-door-key={row.key}
                                        data-date={ds}
                                        className="min-h-[3rem] min-w-0 space-y-1 overflow-hidden border-b border-r p-1"
                                    >
                                        {list.map((a) => {
                                            const s = parseISO(a.scheduledStartUtc);
                                            const e = parseISO(a.scheduledEndUtc);
                                            const isDragging = drag?.appt.appointmentId === a.appointmentId && drag.moved;
                                            return (
                                                <div
                                                    key={a.appointmentId}
                                                    onPointerDown={(ev) => {
                                                        if (ev.button !== 0) return;
                                                        ev.stopPropagation();
                                                        setDrag({ appt: a, originX: ev.clientX, originY: ev.clientY, x: ev.clientX, y: ev.clientY, moved: false });
                                                    }}
                                                    onContextMenu={(ev) => {
                                                        ev.preventDefault();
                                                        onRequestContextMenu({ appt: a, x: ev.clientX, y: ev.clientY });
                                                    }}
                                                    /* ⭐ DOUBLE CLICK OPENS THE EDITOR (Pete,
                                                       2026-09-27) — see the prop's note.
                                                       ⛔ `stopPropagation` or the CELL beneath
                                                       takes it as a double-click on empty slot
                                                       time, which is the create gesture — the
                                                       same guard the Gantt chip carries. */
                                                    onDoubleClick={(ev) => {
                                                        ev.stopPropagation();
                                                        onEditAppointment?.(a);
                                                    }}
                                                    onMouseEnter={(ev) => hover.onEnter(ev, a)}
                                                    onMouseLeave={hover.onLeave}
                                                    className={`overflow-hidden rounded px-1.5 py-0.5 text-[11px] leading-tight ${chipClass(a)} ${
                                                        isDragging ? 'opacity-40' : ''
                                                    }`}
                                                    style={{ cursor: 'grab', touchAction: 'none' }}
                                                    title={
                                                        fetchEvents
                                                            ? undefined
                                                            : a.direction === 'BLOCK'
                                                              ? a.comments || 'Blocked'
                                                              : `${a.carrierName || a.carrierCode} → ${a.clientName || a.clientCode}`
                                                    }
                                                >
                                                    <div className="flex items-center gap-1 truncate">
                                                        <DirTag dir={a.direction} />
                                                        {/* ⭐ ONE SLOT, THREE ANSWERS — see the note in GanttCalendar. */}
                    {a.laborActive ? <LaborPulse /> : a.laborPaused ? <LaborPausedMark /> : a.laborDone ? <LaborDoneMark /> : null}
                                                        <DriverBadge appt={a} />
                                                        {/* ⭐ Same three marks, same order as the day board. */}
                                                        <PaperworkBadge appt={a} />
                                                        <LicenceBadge appt={a} />
                                                        {/**
                                                          * ---- ⭐ THE "NO WMS" MARK REACHES WEEK AND MONTH -------
                                                          *
                                                          * > Pete, 2026-09-27, asked for it on all three views. It
                                                          * > had only ever been wired into `GanttCalendar`, and
                                                          * > nothing said so — the test sheet's B8 line promised
                                                          * > "Day, Week and Month" about the CHIP ROW, and I let
                                                          * > that stand in for the MARK.
                                                          *   ** A CHECKLIST LINE THAT IS TRUE OF THE NEIGHBOUR
                                                          *      FEATURE READS AS TRUE OF THIS ONE. **
                                                          *
                                                          * ⛔ INLINE HERE, NOT IN A CORNER. The day tile is a
                                                          * `relative` lane with real height, so it can carry an
                                                          * absolutely-positioned mark lower-right. A week chip is
                                                          * THREE LINES OF TEXT in an `overflow-hidden` box with no
                                                          * `relative` — an absolute mark would either escape to
                                                          * some ancestor or sit on top of the sub-line. So it takes
                                                          * the badge row, immediately after the licence mark, which
                                                          * is where the eye already looks for tile marks.
                                                          *   ** THE SAME MEANING DOES NOT OWE YOU THE SAME
                                                          *      COORDINATES. THE CONTAINER DECIDES. **
                                                          *
                                                          * ⚠️ `h-3.5` + `p-0.5` matches the three badges' FOOTPRINT
                                                          * (their `h-3` + `px-1 py-0.5`) to within a pixel, so the
                                                          * row does not grow when a truck loses its order.
                                                          *
                                                          * ⛔ `null` still draws nothing — Week and Month share the
                                                          * same `AppointmentDto`, so a payload that never computed
                                                          * `hasWmsOrder` must not be stamped "no order" here either.
                                                          */}
                                                        {/* ⭐ 2026-09-28 — the same NO WMS pill as the day board (WmsMarks.tsx). */}
                                                        {/* ⚠️ 8px, no tracking: THIS board's IN / OUT tag (DirTag), not the day board's. */}
                                                        {a.direction !== 'BLOCK' && <NoWmsBadge hasWmsOrder={a.hasWmsOrder} className="text-[8px] tracking-normal" />}
                                                        <span className="truncate font-medium tabular-nums">
                                                            {format(s, 'h:mm')}–{format(e, 'h:mma')}
                                                        </span>
                                                    </div>
                                                    <div className="truncate">
                                                        {a.direction === 'BLOCK'
                                                            ? a.comments || 'Blocked'
                                                            : `${a.carrierCode}${
                                                                  a.clientName || a.clientCode ? ` · ${a.clientName || a.clientCode}` : ''
                                                              }`}
                                                    </div>
                                                    <div className="truncate text-[9px] opacity-80">{subLine(a)}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    ))}

                    {/**
                      * ---- ⛔ THE SAME EMPTY STATE THE DAY BOARD GOT (Pete, 2026-09-19) --
                      *
                      * ⛔ The fault was reported on Day, but it is a property of the DATA,
                      * not of the view: a dock-enabled facility with no `dock.DockDoor`
                      * rows draws an empty board on all three. Fixing only the screen the
                      * report came from leaves the same unanswerable blank one click away.
                      *
                      *   ** A DEFECT REPORTED ON ONE VIEW OF SHARED DATA IS A DEFECT ON
                      *      EVERY VIEW OF IT. THE VIEW IS WHERE IT WAS SEEN, NOT WHERE IT
                      *      LIVES. **
                      *
                      * ⚠️ NOT SHARED AS A COMPONENT WITH THE GANTT'S, DELIBERATELY. That
                      * one is absolutely positioned under a computed row offset; this one is
                      * a grid child spanning all eight columns. The WORDS are what must
                      * agree, and they are asserted to — see `tools/tenancy-row3-guard.cjs`.
                      * ⛔ Hoisting the markup would mean one component carrying two layout
                      * models and a prop to pick between them, which is how §1AV.170's
                      * duplicate-component fault started.
                      */}
                    {doors.length === 0 && (
                        <div className="col-span-8 flex justify-center border-b bg-card px-6 py-6">
                            <div className="max-w-md rounded-lg border border-dashed p-4 text-center">
                                <p className="text-sm font-medium">
                                    No dock doors are set up for this facility
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground">
                                    The schedule loaded correctly — this site just has no doors
                                    configured yet. Add them in Settings → Doors, or pick a
                                    different facility above.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Drag ghost */}
            {drag?.moved && (
                <div
                    className={`pointer-events-none fixed z-50 rounded px-1.5 py-0.5 text-[11px] leading-tight shadow-lg ${chipClass(drag.appt)}`}
                    style={{ left: drag.x + 8, top: drag.y + 8, maxWidth: '10rem' }}
                >
                    <div className="font-medium tabular-nums">
                        {format(parseISO(drag.appt.scheduledStartUtc), 'h:mm')}–{format(parseISO(drag.appt.scheduledEndUtc), 'h:mma')}
                    </div>
                    <div className="truncate">
                        {drag.appt.direction === 'BLOCK'
                            ? drag.appt.comments || 'Blocked'
                            : drag.appt.carrierName || drag.appt.carrierCode}
                    </div>
                </div>
            )}

            {hover.node}
        </div>
    );
}
